import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { IITH } from '@/pages/landing/iithBrand';
import { referralApprovalApi } from '../api/referralApproval.api';
import ApprovalStageFields from './ApprovalStageFields';

const EMPTY = {
  review_1: {
    enabled: true, assignee_type: 'role', role_key: 'fic', role_code: 'fic',
    user_id: '', user_name: '',
  },
  review_2: {
    enabled: false, assignee_type: 'role', role_key: 'dean_admin', role_code: 'dean_admin',
    user_id: '', user_name: '',
  },
  approval: {
    assignee_type: 'role', role_key: 'director', role_code: 'director',
    user_id: '', user_name: '',
  },
};

function resolveAssignee(stage, roles = []) {
  const a = { ...stage };
  if ((a.assignee_type || 'role') !== 'role' || !a.role_key) return a;
  const hit = roles.find(
    (r) => r.role_key === a.role_key || (r.code && r.code === a.role_key)
      || (r.code && a.role_code && r.code === a.role_code),
  );
  if (hit) {
    a.role_key = hit.role_key;
    a.role_code = hit.code || a.role_code || '';
  }
  return a;
}

function mergeForm(cfg, roles = []) {
  const merged = {
    ...EMPTY,
    ...cfg,
    review_1: { ...EMPTY.review_1, ...(cfg?.review_1 || {}) },
    review_2: { ...EMPTY.review_2, ...(cfg?.review_2 || {}) },
    approval: { ...EMPTY.approval, ...(cfg?.approval || {}) },
  };
  return {
    ...merged,
    review_1: resolveAssignee(merged.review_1, roles),
    review_2: resolveAssignee(merged.review_2, roles),
    approval: resolveAssignee(merged.approval, roles),
  };
}

/** Referral approval stages — own load + save for Configuration screen. */
export default function ReferralApprovalConfigSection() {
  const [form, setForm] = useState(EMPTY);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([referralApprovalApi.getConfig(), referralApprovalApi.options()])
      .then(([cfg, opts]) => {
        const roleList = opts.roles || [];
        setRoles(roleList);
        setUsers(opts.users || []);
        setForm(mergeForm(cfg, roleList));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load referral approval config'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      setForm(mergeForm(await referralApprovalApi.saveConfig(form), roles));
      toast.success('Referral approval configuration saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save referral approval config');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="py-12 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="referral-approval-config-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[#071B34]">Referral approvals</h3>
          <p className="text-sm text-[#5A6B7C] mt-0.5">
            Review 1 and Review 2 are optional. Final Approval is required, then the preferred
            doctor prepares the referral letter.
          </p>
        </div>
        <Button onClick={save} disabled={saving} style={{ background: IITH.saffron }}
          className="text-white" data-testid="referral-approval-section-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save referral approvals
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <ApprovalStageFields
          title="Review 1"
          hint="Optional first review for out-station referrals"
          value={form.review_1}
          onChange={(v) => setForm((f) => ({ ...f, review_1: v }))}
          roles={roles}
          users={users}
          optional
        />
        <ApprovalStageFields
          title="Review 2"
          hint="Optional second review"
          value={form.review_2}
          onChange={(v) => setForm((f) => ({ ...f, review_2: v }))}
          roles={roles}
          users={users}
          optional
        />
        <ApprovalStageFields
          title="Approval"
          hint="Final approval — then moves to the preferred doctor for the letter"
          value={form.approval}
          onChange={(v) => setForm((f) => ({ ...f, approval: v }))}
          roles={roles}
          users={users}
        />
      </div>
    </div>
  );
}
